// REACT IMPORTS
import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import ReactSelect from 'react-select';
import classNames from 'classnames';

// REDUX IMPORTS 
import { GetUploadEndpoint, UploadFileToProvider } from '../../../connection/uploads';
import { CreateNewPasscodeList, SendUnsentMagicLinkRegistrationEmails } from '../../../connection/passcode-lists';
import { useAppDispatch, useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { GetDefaultPasscodeList } from '../../../store/utils/create-event';
import { getMagicLinkRegistrationPasscodeList, getPasscodeLists } from '../../../store/actions/admin/passcode-lists';
import { addRegistrationRequirement, getPublishedEventUrl, loadWorkingEvent, setRegistrationType, updateRegistrationSettings } from '../../../store/actions/admin/create-event';

// FUNCTIONALITY IMPORTS 
import { PASSCODE_LIST_TYPE, WorkingPasscodeList, PasscodeList, GateTypes, RegistrationTypes, BrandliveEventListItem, ManuallyAddedUser } from '../../../types/working-model';
import { getUploadFilename, isValidEmail } from '../../../utils/utils';
import { OptionalComponent } from '../../../utils/optional-component';
import LargeButton from '../../general-ui/button/large-button';
import TextInput, { Validation } from '../../general-ui/text-input/text';
import { SelectOption } from '../../general-ui/select/select';
import Modal from '../../general-ui/modal/modal';
import { showAlert } from '../../general-ui/alert/alert-service';
import { DropdownOption } from "../../general-ui/dropdown/dropdown";
import { PrimaryTooltip } from '../create/session/session-modal';
import FileInput from '../../../utils/file-input';
import { reactSelectStyles, TReactSelectOption, adminModalMultiSelectStyling } from '../../general-ui/react-select/react-select-styles';
import { generateMagicLinksForEvent, getMagicLinksForEvent } from '../../../store/actions/admin/magic-links';
import { DownloadCsvButton } from '../create/registration/download-csv-button';
import { SetItemContentType } from '../../../connection/multicloud/upload';
import { isArray } from 'underscore';
import SelectInput from '../../general-ui/select/select';
import Icon, { ICONS, COLORS } from '../../general-ui/icon';
import { useIsAudienceListsUiQ32023 } from "./hooks/audience.hooks";

import './add-passcode-list.scss';
import { getPasscodeListMembers, searchMemberCount } from 'store/actions/admin/passcode-list-members';
import TagSelectInput from '@general-ui/tag-select/tag-select';
import { batch } from 'react-redux';
import { FetchError } from 'connection/helpers';

interface PasscodeListProps {
	setModalClosedSubmit: () => void;
	setModalClosedCancel: () => void;
	modalOpen: boolean;
	attachToEvent?: (list: PasscodeList) => void; // Adding for next ticket where we will connect the list on creation
	magicLinkRegistration?: boolean;
	order?: number | null;
	setToUploadedPill?: () => void;
}


const passcodeListTypeOptions: SelectOption[] = [
	{
		label: 'Email',
		value: PASSCODE_LIST_TYPE.EMAILS_ONLY.toString(),
	},
	{
		label: 'Passcode',
		value: PASSCODE_LIST_TYPE.PASSCODES_ONLY.toString(),
	},
	{
		label: 'Email & Passcode',
		value: PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES.toString(),
	},
	{
		label: 'Email & Magic Link Guard',
		value: PASSCODE_LIST_TYPE.MAGIC_LINKS.toString(),
	},
];

const passcodeListTypeOptionsV2: SelectOption[] = [
	{
		label: 'Email',
		value: PASSCODE_LIST_TYPE.EMAILS_ONLY.toString(),
	},
	{
		label: 'Email & Passcode',
		value: PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES.toString(),
	},
	{
		label: 'Passcode',
		value: PASSCODE_LIST_TYPE.PASSCODES_ONLY.toString(),
	},
	{
		label: 'Email & Magic Link Guard',
		value: PASSCODE_LIST_TYPE.MAGIC_LINKS.toString(),
	},
];

export default function PasscodeListCreator(props: PasscodeListProps): JSX.Element {
	const { setModalClosedSubmit, setModalClosedCancel, modalOpen, magicLinkRegistration, order, setToUploadedPill } = props;
	const user = useTypedSelector(state => state.AuthReducer.user);
	const passcodeLists = useTypedSelector(state => state.PasscodeListReducer.passcodeLists);
	const events = useTypedSelector(state => state.EventsReducer.eventsList);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const publishedUrl = useTypedSelector(state => state.CreateEventReducer.publishedUrl);
	const fetchingPublishedStatus = useTypedSelector(state => state.CreateEventReducer.fetchingPublishedStatus);
	const magicLinkRegistrationPasscodeList = useTypedSelector(state => state.CreateEventReducer.workingEvent)?.registration_settings?.magic_links_registration_passcode_list;

	const csvUpload = useRef<HTMLInputElement | null>(null);

	const [workingPasscodeList, setWorkingPasscodeList] = useState<WorkingPasscodeList>(GetDefaultPasscodeList());
	const [loading, setLoading] = useState(false);
	const [selectedEventUuid, setSelectedEventUuid] = useState<string>('');
	const [selectedEventName, setSelectedEventName] = useState<string>('');
	const [sendConfirmationModalOpen, setSendConfirmationModalOpen] = useState(false);
	const [sendTestEmailModalOpen, setSendTestEmailModalOpen] = useState(false);

	const [testEmailAddresses, setTestEmailAddresses] = useState<string[]>([]);


	const dispatch = useAppDispatch();
	const isAudiencesUiQ32023 = useIsAudienceListsUiQ32023();

	const [displayListManipulation, setDisplayListManipulation] = useState<boolean>(true);



	useEffect(() => {
		const eventHasBeenPublished = Boolean(publishedUrl);
		const eventSelected = Boolean(selectedEventUuid);
		if (
			[PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(workingPasscodeList.type)
			&& !eventHasBeenPublished
			&& eventSelected
		) {
			setDisplayListManipulation(false);
		} else {
			setDisplayListManipulation(true);
		}
	}, [setDisplayListManipulation, publishedUrl, workingPasscodeList, selectedEventUuid]);

	useEffect(() => {
		if (selectedEventUuid && token && (!workingEvent || workingEvent.uuid !== selectedEventUuid)) {
			// Need to pulling workingEvent when a magic link list is being created
			dispatch(loadWorkingEvent(selectedEventUuid, token));
		}
	}, [selectedEventUuid, token, dispatch, workingEvent]);


	// Initiates setting the event for magic links
	const handleEventSelectionChange = useCallback((option: DropdownOption | null) => {
		if (option && token) {
			const eventUuid = option.value.toString();

			// Must check to see if the event has a published URL (during which Apply is disabled)
			dispatch(getPublishedEventUrl(token, eventUuid));

			// Both published and never-published events can sit in the select until the check is complete
			setSelectedEventUuid(eventUuid);
			setSelectedEventName(option.label);
		}
	}, [dispatch, token]);

	async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target?.files?.[0];

		if (file && token) {
			const endpoint = await GetUploadEndpoint(
				token,
				getUploadFilename(file.name)
			);
			const finalUrl = await UploadFileToProvider(
				endpoint,
				file,
			);
			SetItemContentType(token, finalUrl, file.type).catch(console.error);
			setWorkingPasscodeList((list: WorkingPasscodeList) => ({
				...list,
				url: finalUrl,
			}));
		}
	}

	async function onFile(file: File | FileList) {
		if (file instanceof File && token) {
			setLoading(true);
			const endpoint = await GetUploadEndpoint(
				token,
				getUploadFilename(file.name)
			);

			const finalUrl = await UploadFileToProvider(
				endpoint,
				file,
			);
			SetItemContentType(token, finalUrl, file.type).catch(console.error);
			setWorkingPasscodeList((list: WorkingPasscodeList) => ({
				...list,
				url: finalUrl,
			}));
			setLoading(false);
		}
	}

	const handlePasscodeListType = useCallback((value: string | number) => {
		switch (value) {
			case PASSCODE_LIST_TYPE.EMAILS_ONLY:
			case PASSCODE_LIST_TYPE.EMAILS_ONLY.toString(): {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.EMAILS_ONLY,
				}));
				break;
			}
			case PASSCODE_LIST_TYPE.PASSCODES_ONLY:
			case PASSCODE_LIST_TYPE.PASSCODES_ONLY.toString(): {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.PASSCODES_ONLY,
				}));
				break;
			}
			case PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES:
			case PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES.toString(): {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES,
				}));
				break;
			}
			case PASSCODE_LIST_TYPE.MAGIC_LINKS:
			case PASSCODE_LIST_TYPE.MAGIC_LINKS.toString(): {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.MAGIC_LINKS,
				}));
				break;
			}
			case PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION:
			case PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION.toString(): {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION,
				}));
				break;
			}
		}
	}, []);

	useEffect(() => {
		if (magicLinkRegistration && modalOpen) {
			//we dealing with a magic link registration list so force select of the type and set the event select to our event
			handlePasscodeListType(PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION.toString());
			if (workingEvent) {
				handleEventSelectionChange({ label: workingEvent.name || '', value: workingEvent.uuid || '' });
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					name: `${workingEvent.uuid} - Magic Link Registration`,
				}));
			}

		} else if (!modalOpen) {
			//reset defaults after close
			setWorkingPasscodeList(GetDefaultPasscodeList());
		}
	}, [handlePasscodeListType, handleEventSelectionChange, magicLinkRegistration, modalOpen, workingEvent]);


	function handlePasscodeListName(e: React.ChangeEvent<HTMLInputElement>) {
		setWorkingPasscodeList((list: WorkingPasscodeList) => ({
			...list,
			name: e.target.value,
		}));
	}

	function handlePasscodeListMasterPasscode(e: React.ChangeEvent<HTMLInputElement>) {
		setWorkingPasscodeList((list: WorkingPasscodeList) => ({
			...list,
			master_passcode: e.target.value.toLocaleLowerCase(),
		}));
	}

	const eventOptions: TReactSelectOption[] = useMemo(() => {
		const list = events.filter(event => event.registration_on && event.required_registration_questions).map((event: BrandliveEventListItem) => {
			return {
				label: event.name.toString(),
				value: event.uuid.toString()
			};
		});
		return list;
	}, [events]);

	function setManuallyAddedUserValue(index: number, key: 'email' | 'first_name' | 'last_name') {
		return (event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;

			setWorkingPasscodeList(workingPasscodeList => {
				workingPasscodeList.manually_added_users[index][key] = value;
				return {
					...workingPasscodeList,
					manually_added_users: [...workingPasscodeList.manually_added_users]
				};
			});
		};
	}

	function addManuallyAddedUser() {
		setWorkingPasscodeList(workingPasscodeList => {
			return {
				...workingPasscodeList,
				manually_added_users: [
					...(workingPasscodeList?.manually_added_users || []),
					{
						email: '',
						first_name: '',
						last_name: ''
					}
				]
			};
		});
	}

	function removeManuallyAddedUser(index: number) {
		return () => {
			setWorkingPasscodeList(workingPasscodeList => {
				if (workingPasscodeList?.manually_added_users) {
					return {
						...workingPasscodeList,
						manually_added_users: workingPasscodeList?.manually_added_users.filter((user: ManuallyAddedUser, i: number) => i !== index)
					};
				} else {
					return workingPasscodeList;
				}
			});
		};
	}

	async function finished(isTestEmailSend = false) {
		if (!token || !user?.active_channel) return;

		// A magic links list can't be applied to an event that doesn't have a URL
		const isMagicLinksList = [PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(workingPasscodeList.type);
		const hasNeverBeenPublished = selectedEventUuid && !publishedUrl;
		if (isMagicLinksList && hasNeverBeenPublished) {
			return showAlert({
				message: 'Please Publish Project First',
				description: `You need to publish ${selectedEventName} first before you can attach a magic links list`,
				type: 'error',
				duration: 5000,
			});
		}

		const listToCreate = {
			...workingPasscodeList,
			name: workingPasscodeList.name.trim(),
		};

		if (isTestEmailSend) {
			//If we are dealing with a test email, we want to complete the process for only the emails added in the test email modal.
			listToCreate.url = '';
			listToCreate.manually_added_users = testEmailAddresses.map(email => ({ email, first_name: 'Test', last_name: 'Email' }));
		}

		// Check for empty name
		if (!listToCreate.name) {
			return showAlert({
				message: 'Name Required',
				description: 'Please enter a unique name for this audience list.',
				type: 'error',
				duration: 5000,
			});
		}

		// Check for duplicate list name
		const lowerCaseName = listToCreate.name.toLowerCase();
		const isDuplicate = passcodeLists.some((list) => {
			return lowerCaseName === list.name?.toLowerCase() && !magicLinkRegistrationPasscodeList;
		});
		if (isDuplicate) {
			return showAlert({
				message: 'Duplicate Audience List Name',
				description: `"${listToCreate.name}" is already in use. Please enter a unique name.`,
				type: 'error',
				duration: 5000,
			});
		}

		setLoading(true);
		let newPasscodeList: any = null;
		try {
			newPasscodeList = await CreateNewPasscodeList(
				token,
				user.active_channel,
				listToCreate,
				selectedEventUuid,
				listToCreate.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION ? magicLinkRegistrationPasscodeList : undefined
			);
		} catch (e) {
			if (e instanceof FetchError) {
				showAlert({
					message: 'Unable to save the list',
					description: e.error ? e.error : 'Failed to create new audience list',
					type: 'error',
					duration: 10000,
				});
			}

			setSendConfirmationModalOpen(false);
			setLoading(false);
			return;
		}

		const downloadErrorCSV = (newPasscodeList: any) => {
			// Downloads a csv file with all the broken users
			if (newPasscodeList.csv) {
				const blob = new Blob([newPasscodeList["csv"]], { type: 'text/csv;charset=utf-8' });
				const link = document.createElement('a');
				link.setAttribute('href', URL.createObjectURL(blob));
				link.setAttribute('download', 'magic-link-nonvalid-emails.csv');
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		};

		if ("csv" in newPasscodeList) {
			showAlert({
				message: 'Unable to Create',
				description: newPasscodeList.error ? newPasscodeList.error : 'Failed to create new audience list',
				type: 'error',
				duration: 10000,
				onClick: () => downloadErrorCSV(newPasscodeList)
			});
			return setLoading(false);
		}
		else if ("error" in newPasscodeList) {
			showAlert({
				message: 'Unable to Create',
				description: newPasscodeList.error ? newPasscodeList.error : 'Failed to create new audience list',
				type: 'error',
				duration: 5000,
			});
			return setLoading(false);
		}

		if (isArray(newPasscodeList.failedEmails) && newPasscodeList.failedEmails.length > 0) {
			showAlert({
				message: 'Some emails were invalid and failed to upload:',
				description: newPasscodeList.failedEmails.join(' '),
				type: 'error',
				duration: 5000
			});
		}

		if (magicLinkRegistration && newPasscodeList.passcode_list) {
			batch(() => {
				dispatch(
					getPasscodeListMembers(
						token,
						newPasscodeList.passcode_list,
						0,
						order || 0
					)
				);
				dispatch(getMagicLinkRegistrationPasscodeList(token, user.active_channel, newPasscodeList.passcode_list));
				dispatch(
					searchMemberCount(
						token,
						Number(newPasscodeList?.passcode_list),
						''
					)
				);
			});
		} else {
			dispatch(getPasscodeLists(user.active_channel, token));
		}

		setLoading(false);
		if (isTestEmailSend) {
			setTestEmailAddresses([]);
			setSendTestEmailModalOpen(false);
		} else {
			setWorkingPasscodeList(GetDefaultPasscodeList());
			setModalClosedSubmit();
			setSendConfirmationModalOpen(false);

			if (setToUploadedPill) { // If we are in the upload flow, we want to set the uploaded pill
				setToUploadedPill();
			}
		}

		// ATTACH EVENT 
		if (![PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(newPasscodeList.type)) {
			return;
		}

		if (!workingEvent || !newPasscodeList.passcode_list) {
			return;
		}

		if (newPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS) {
			const sessionsIDs = workingEvent ? workingEvent?.sessions.map(sess => sess.session) : []; // Gets all the session ids in the event

			const workingGateRequirement = {
				type: GateTypes.passcode_list,
				passcodeList: [newPasscodeList.passcode_list],
				gatedSessions: [...sessionsIDs]
			};

			// Wait for the registration requirements to get added

			dispatch(addRegistrationRequirement(workingGateRequirement.type, workingGateRequirement));
			dispatch(setRegistrationType(RegistrationTypes.gated));
		} else if (newPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION) {
			//store the ref to the magic link registration passcode_list in registration settings since it is not a true gate
			if (workingEvent.registration_settings) {
				const updatedSettings = {
					...workingEvent.registration_settings,
					magic_links_registration_passcode_list: newPasscodeList.passcode_list
				};
				dispatch(updateRegistrationSettings(token, selectedEventUuid, updatedSettings));
			}
		}

		if (token && user && newPasscodeList && newPasscodeList.passcode_list) {
			// generate the magic links
			await dispatch(generateMagicLinksForEvent(token, user?.active_channel, newPasscodeList?.passcode_list, selectedEventUuid, newPasscodeList.type));
			if (workingEvent && publishedUrl && newPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION) {
				try {
					// send the magic link registration emails
					const response = await SendUnsentMagicLinkRegistrationEmails(token, workingEvent?.uuid, newPasscodeList.passcode_list);
					if (response.success) {
						// "message" will only exist for magic link registrations
						if (newPasscodeList.message) {
							showAlert({
								message: 'Successfully uploaded list members',
								description: newPasscodeList.message,
								duration: 10000
							});
						} else {
							showAlert({
								message: `Successfully sent magic link emails`,
								description: `New users will receive an invite with a magic link inside`,
								type: 'text',
								duration: 10000,
							});
						}
					} else {
						showAlert({
							message: `We were unable to send some or all magic link emails`,
							description: `Please re-trigger the email send by clicking the "Send unsent invites" button. If the problem persists contact support`,
							type: 'error',
							duration: 10000,
						});
					}
				} catch (e) {
					showAlert({
						message: 'Error',
						description: 'There was an error sending the magic link registration emails',
						type: 'error',
						duration: 5000,
					});
				}

				dispatch(getMagicLinksForEvent(token, user?.active_channel, newPasscodeList.passcode_list, workingEvent?.uuid));
			}
		}

		if (newPasscodeList.type !== PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION) {
			showAlert({
				message: 'Attaching list to event',
				description: `Your list is attached to the event ${workingEvent?.name}`,
				type: 'text',
				duration: 5000,
			});
		}

	}

	function openSendConfirmationModal() {
		setSendConfirmationModalOpen(true);
	}

	function closeSendConfirmationModal() {
		setSendConfirmationModalOpen(false);
	}

	function openSendTestEmailModal() {
		setSendTestEmailModalOpen(true);
	}

	function closeSendTestEmailModal() {
		setSendTestEmailModalOpen(false);
	}

	const anyFieldHasValue = (user: ManuallyAddedUser): boolean => {
		if (user) {
			return Object.values(user).some((value) => value.length > 0);
		}

		return false;
	};


	const allListMembersValid = (): boolean => {
		let valid = true;
		const passcodeListType = workingPasscodeList?.type;
		const manuallyAddedUsers = workingPasscodeList?.manually_added_users;
		const partiallyFilledManualUsers = workingPasscodeList?.manually_added_users.filter((user) => anyFieldHasValue(user));

		//if we don't have an upload url AND we don't have any partially filled out manually added users, dont allow save
		if (!workingPasscodeList.url.length && !partiallyFilledManualUsers.length) {
			return false;
		}

		if (partiallyFilledManualUsers.length) {
			//if email is required, all users should have a valid email
			valid = partiallyFilledManualUsers.every((user) => {
				return isValidEmail(user.email);
			});
		}

		return valid;
	};

	const manuallyAddedUserIsValid = (user: ManuallyAddedUser): boolean => {
		//if a manually added user has any field filled out, the user is valid only if they have a valid email
		if (anyFieldHasValue(user)) {
			return user.email.length > 0 && isValidEmail(user.email);
		}
		return true;
	};


	const noNameSet = workingPasscodeList.name?.trim() === '';
	const noEventSet = selectedEventUuid === '';
	const isMagicLinkEventNotSet = workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS && (noEventSet || fetchingPublishedStatus);
	const isMagicLinkRegistrationInvalid = workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION
		&& !allListMembersValid();

	const listTypeOptions = isAudiencesUiQ32023 ? passcodeListTypeOptionsV2 : passcodeListTypeOptions;

	return (
		<>
			<Modal
				className={classNames('add-passcode-list-modal', { 'add-passcode-list-modal-v2': isAudiencesUiQ32023 })}
				open={modalOpen}
				onRequestClose={setModalClosedCancel}
				padTitle={false}
				cancellable={true}
				closeable={false}
				header={
					<>
						<OptionalComponent display={displayListManipulation && !magicLinkRegistration}>
							<h4>Add List</h4>
							<p className="modal-subtitle">A list can be used give a group of people access to projects and sessions</p>
						</OptionalComponent>
						<OptionalComponent display={displayListManipulation && magicLinkRegistration}>
							<h4>Add Registrants</h4>
							<p className="modal-subtitle">Register your target audience seamlessly. The platform will accept batches of 1,000 at a time.</p>
						</OptionalComponent>
						<OptionalComponent display={!displayListManipulation}>
							<h4>Publish Project</h4>
						</OptionalComponent>
					</>
				}
				footer={
					<>
						<button onClick={setModalClosedCancel} style={{ marginRight: 16 }}>
							Cancel
						</button>
						<OptionalComponent display={displayListManipulation}>
							<button
								disabled={
									loading
									|| noNameSet
									|| isMagicLinkEventNotSet
									|| isMagicLinkRegistrationInvalid
								}
								onClick={workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION ? openSendConfirmationModal : () => finished()}
								className="lemonade"
							>
								Save
							</button>
						</OptionalComponent>
					</>
				}
			>
				<div className="add-audience-list-form">
					<OptionalComponent display={!magicLinkRegistration}>
						<TextInput
							label={'List name'}
							onChange={handlePasscodeListName}
							placeholder={isAudiencesUiQ32023 ? 'Give your list a name' : 'List name'}
							value={workingPasscodeList.name}
						/>
						<SelectInput
							label={listTypeOptions ? 'List type' : ''}
							mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={14} color="white" />}
							iconIfOpen={<Icon name={ICONS.KEYBOARD_ARROW_UP} size={14} color={COLORS.WHITE} />}
							options={listTypeOptions}
							selected={workingPasscodeList?.type.toString()}
							noMaxHeight
							onChange={handlePasscodeListType}
							highlightSelectedItem={isAudiencesUiQ32023}
						/>
					</OptionalComponent>

					<OptionalComponent display={![PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION, PASSCODE_LIST_TYPE.EMAILS_ONLY,].includes(workingPasscodeList.type)}>
						<TextInput
							label={isAudiencesUiQ32023 ? 'Global Passcode' : 'Master Passcode'}
							tooltip={[PrimaryTooltip, 'Note: master passcodes are not case sensitive']}
							onChange={handlePasscodeListMasterPasscode}
							placeholder={isAudiencesUiQ32023 ? 'Enter global passcode' : 'Enter master passcode'}
							value={workingPasscodeList.master_passcode}
						/>
					</OptionalComponent>
					<OptionalComponent display={!magicLinkRegistration && [PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(workingPasscodeList?.type)}>
						<div className="invite-language-selector">
							{isAudiencesUiQ32023 && <label>Project</label>}
							<ReactSelect
								onChange={handleEventSelectionChange}
								options={eventOptions}
								placeholder={'Select event to attach'}
								closeMenuOnSelect={true}
								styles={reactSelectStyles(adminModalMultiSelectStyling({ plainTextValue: isAudiencesUiQ32023 }))}
								value={selectedEventUuid ? { label: selectedEventName, value: selectedEventUuid } : null}
								classNamePrefix="invite-language-select"
								className="invite-language-select"
							/>
						</div>
					</OptionalComponent>
				</div>
				<OptionalComponent display={displayListManipulation}>
					<div className={classNames({ "magic-link-registration-bulk-upload-container": magicLinkRegistration })}>
						<div className="bulk-upload-header">BULK UPLOAD</div>
						<LargeButton
							title=""
							customTitle={<div className="upload-primary-text">
								<span className="plus">+ </span>
								<span>Upload an audience list</span>
							</div>}
							subtitle={'or drag and drop here in .csv file format'}
							onFile={onFile}
							allowedFileTypes={[
								'text/csv',
								'application/vnd.ms-excel',
							]}
							multiple={false}
						/>
						<FileInput
							type="file"
							onChange={handleFileChange}
							ref={csvUpload}
							accept={["application/csv"]}
							style={{ display: 'none' }}
						/>
						<div className="download-template-container">
							<OptionalComponent display={!magicLinkRegistration}>
								<p className="add-item info">If you're planning to generate magic links, please download the dynamic template</p>
							</OptionalComponent>
							<OptionalComponent display={magicLinkRegistration}>
								<p className="add-item info">Please utilize the template for best results</p>
							</OptionalComponent>
							<DownloadCsvButton
								eventUuid={selectedEventUuid}
								listType={workingPasscodeList.type}
								buttonText={isAudiencesUiQ32023 ? 'Download template' : undefined}
								classNames='small download-template-button'
							/>
						</div >
					</div >

					<OptionalComponent display={workingPasscodeList.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION}>
						<div className="manually-added-users-header">ADD MANUALLY</div>
						<div className="manually-added-users-table">
							<div className="manually-added-users-col-header">
								<div className="manually-added-users-col-header-email">Email</div>
								<div className="manually-added-users-col-header-name">First name</div>
								<div className="manually-added-users-col-header-name">Last name</div>

							</div>
							{
								workingPasscodeList?.manually_added_users && (
									<>
										<div className="manually-added-users">
											{
												workingPasscodeList.manually_added_users.map((user: ManuallyAddedUser, index: number) => (
													<div className="manually-added-user" key={index}>
														<TextInput
															className="manually-added-user-email"
															//label={'Email'}
															onChange={setManuallyAddedUserValue(index, 'email')}
															placeholder={'Email'}
															value={user.email}
															valid={manuallyAddedUserIsValid(user) ? Validation.normal : Validation.error}
															required={user.email.length > 0} //only require if they have entered something
														/>
														<TextInput
															className="manually-added-user-name"
															onChange={setManuallyAddedUserValue(index, 'first_name')}
															placeholder={'First name'}
															value={user.first_name}
														/>
														<TextInput
															className="manually-added-user-name"
															onChange={setManuallyAddedUserValue(index, 'last_name')}
															placeholder={'Last name'}
															value={user.last_name}
														/>
														<OptionalComponent display={index === 0}>
															<div className="manually-added-spacer"></div>
														</OptionalComponent>
														<OptionalComponent display={index !== 0}>
															<button className="clear" onClick={removeManuallyAddedUser(index)}><Icon name={ICONS.CLOSE} size={20} color={COLORS.WHITE} /></button>
														</OptionalComponent>
													</div>
												))
											}
										</div>
									</>

								)
							}
							<button className="small add-attribute" onClick={addManuallyAddedUser}><Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /> Add</button>

						</div>
						<p className="will-send-warning">PLEASE NOTE THAT THIS ACTION WILL AUTOMATICALLY SEND ALL ADDED OR UPLOADED USERS A REGISTRATION CONFIRMATION EMAIL IMMEDIATELY UPON SAVING.</p>
					</OptionalComponent>


				</OptionalComponent >
				<OptionalComponent display={!displayListManipulation}>
					<div className="needs-publish">Please publish this project before uploading registrants.</div>
				</OptionalComponent>
			</Modal >

			<Modal
				className="add-passcode-list-modal"
				open={sendConfirmationModalOpen}
				onRequestClose={closeSendConfirmationModal}
				title="Are you sure you want to send?"
				padTitle={false}
				subtitle=""
				cancellable={false}
				closeable={false}
				footer={
					<>
						<div className="add-passcode-list-modal-footer">
							<button className="send-test-button" onClick={openSendTestEmailModal}>
								<Icon name={ICONS.VIEWERS_EYE} size={20} color={COLORS.WHITE} /><div>Send test email</div>
							</button>
							<div>
								<button onClick={closeSendConfirmationModal} style={{ marginRight: 16 }}>
									Cancel
								</button>
								<button
									disabled={loading}
									onClick={() => finished()}
									className="lemonade"
								>
									Send
								</button>
							</div>
						</div>
					</>
				}
			>
				<div>Be sure to check your registration confirmation email design prior to saving. Sending now will send the email as is.</div>
			</Modal>

			<Modal
				open={sendTestEmailModalOpen}
				onRequestClose={closeSendTestEmailModal}
				cancellable
				closeable={false}
				title="Send Test Email"
				padTitle={false}
				footer={
					<>
						<button onClick={closeSendTestEmailModal}>Cancel</button>
						<button
							onClick={() => finished(true)}
							className="lemonade"
							disabled={!testEmailAddresses.length}
						>
							Save
						</button>
					</>
				}
			>
				<p>Project must be published to reflect any email changes.</p>
				<TagSelectInput
					placeholder="Enter emails"
					onChange={testEmailAddresses => setTestEmailAddresses(testEmailAddresses)}
					tags={testEmailAddresses}
					defaultTags={testEmailAddresses}
					validateEmail
				/>
			</Modal>
		</>
	);
}
